/**
 * ABN/ACN Lookup Service
 * Integrates with the live KYB API (same backend as C1 signup)
 * for real-time ABN/ACN validation and entity details.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ABNLookupResult {
	abn: string;
	name: string;
	legalFirstName?: string;
	legalMiddleNames?: string;
	legalLastName?: string;
	status: string;
	acn?: string;
	businessType?: string;
	address?: string;
	isActive: boolean;
	austracType?: string;
}

export interface ACNLookupResult {
	acn: string;
	name: string;
	legalFirstName?: string;
	legalMiddleNames?: string;
	legalLastName?: string;
	status: string;
	abn?: string;
	businessType?: string;
	address?: string;
	isActive: boolean;
	austracType?: string;
}

// ─── Config ─────────────────────────────────────────────────────────────────

import { getRuntimeConfig } from '../config/runtime';

function getKybApiUrl(): string {
	const config = getRuntimeConfig();
	if (config?.VITE_KYB_API_URL) return config.VITE_KYB_API_URL;
	if (import.meta.env.VITE_KYB_URL) return import.meta.env.VITE_KYB_URL as string;
	throw new Error('KYB API URL not configured. Set VITE_KYB_API_URL in config.json or environment.');
}

const TIMEOUT_MS = 15_000;

// ─── Helpers ────────────────────────────────────────────────────────────────

function isValidABNFormat(abn: string): boolean {
	return abn.replace(/\D/g, '').length === 11;
}

function isValidACNFormat(acn: string): boolean {
	return acn.replace(/\D/g, '').length === 9;
}

/** ACN = last 9 digits of ABN */
function abnToAcn(abn: string): string {
	const clean = abn.replace(/\D/g, '');
	return clean.length === 11 ? clean.slice(2) : '';
}

/** Format ABN: XX XXX XXX XXX */
export function formatABN(abn: string): string {
	const d = abn.replace(/\D/g, '');
	if (d.length !== 11) return d;
	return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8)}`;
}

/** Format ACN: XXX XXX XXX */
export function formatACN(acn: string): string {
	const d = acn.replace(/\D/g, '');
	if (d.length !== 9) return d;
	return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
}

// ─── Lookup Functions ───────────────────────────────────────────────────────

/**
 * Lookup ABN using live ABR / KYB API
 */
export async function lookupABN(abn: string): Promise<ABNLookupResult | null> {
	try {
		const cleanAbn = abn.replace(/\D/g, '');
		if (!isValidABNFormat(cleanAbn)) return null;

		const response = await fetch(getKybApiUrl(), {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ legalEntityCodeType: 'ABN', legalEntityCodeData: cleanAbn }),
			signal: AbortSignal.timeout(TIMEOUT_MS),
		});

		const data = await response.json();

		if (!response.ok || data.action === 'ERROR') {
			return null;
		}

		const statePostcode = [data.stateCode, data.postcode].filter(Boolean).join(' ');

		return {
			abn: data.resultLegalEntityCodeData || cleanAbn,
			name: data.legalEntityName || '',
			legalFirstName: data.legalFirstName || undefined,
			legalMiddleNames: data.legalMiddleNames || undefined,
			legalLastName: data.legalLastName || undefined,
			status: data.legalEntityStatus || 'Active',
			acn: abnToAcn(cleanAbn),
			businessType: data.austracType || data.legalEntityType,
			address: statePostcode,
			isActive: ['active', 'registered'].some(s => (data.legalEntityStatus || '').toLowerCase().includes(s)),
			austracType: data.austracType,
		};
	} catch (error) {
		return null;
	}
}

/**
 * Lookup ACN using live ASIC / KYB API
 */
export async function lookupACN(acn: string): Promise<ACNLookupResult | null> {
	try {
		const cleanAcn = acn.replace(/\D/g, '');
		if (!isValidACNFormat(cleanAcn)) return null;

		const response = await fetch(getKybApiUrl(), {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ legalEntityCodeType: 'ACN', legalEntityCodeData: cleanAcn }),
			signal: AbortSignal.timeout(TIMEOUT_MS),
		});

		const data = await response.json();

		if (!response.ok || data.action === 'ERROR') {
			return null;
		}

		const statePostcode = [data.stateCode, data.postcode].filter(Boolean).join(' ');

		return {
			acn: data.resultLegalEntityCodeData || cleanAcn,
			name: data.legalEntityName || '',
			legalFirstName: data.legalFirstName || undefined,
			legalMiddleNames: data.legalMiddleNames || undefined,
			legalLastName: data.legalLastName || undefined,
			status: data.legalEntityStatus || 'Active',
			abn: data.resultLegalEntityCodeType === 'ABN' ? data.resultLegalEntityCodeData : '',
			businessType: data.austracType || data.legalEntityType,
			address: statePostcode,
			isActive: ['active', 'registered'].some(s => (data.legalEntityStatus || '').toLowerCase().includes(s)),
			austracType: data.austracType,
		};
	} catch (error) {
		return null;
	}
}
